import { Typography, Link, styled } from '@ori-ui/mui';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { TranslationKey } from '../../models';
import { useIsMobile } from '../../utils';

const RootDiv = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const StyledTypography = styled(Typography)<{
  ownerState: { expanded: boolean; visibleMoreButton: boolean; height: number };
}>(({ theme, ownerState: { expanded = false, visibleMoreButton = true, height = 0 } }) => ({
  position: 'relative',
  display: 'block',
  height: expanded ? `${height}px` : visibleMoreButton ? 'calc(3 * 1em * 1.4)' : 'auto',
  lineHeight: 1.4,
  marginTop: theme.spacing(6),
  transition: 'all .2s',
  overflow: visibleMoreButton ? 'hidden' : 'visible',
  marginBottom: visibleMoreButton ? theme.spacing(0) : theme.spacing(6),
}));

const OverlayWrapper = styled('div')<{ ownerState: { expanded: boolean } }>(({ ownerState: { expanded = false } }) => ({
  position: 'absolute',
  top: 0,
  height: expanded ? 0 : 'calc(3em * 1.4)',
  width: '100%',
  background: 'linear-gradient(to bottom, transparent, white)',
}));

const StyledLink = styled(Link)<{ ownerState: { expanded: boolean } }>(
  ({ theme, ownerState: { expanded = false } }) => ({
    paddingBottom: 0,
    color: theme.palette.text.primary,
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginTop: expanded ? theme.spacing(2) : theme.spacing(3),
    marginBottom: theme.spacing(6),
    cursor: 'pointer',
    '& > span': {
      paddingBottom: theme.spacing(1),
      borderBottom: `2px solid ${theme.palette.grey[900]}`,
      lineHeight: 1,
    },
  }),
);

interface IExpandableTextBlock {
  children: JSX.Element | string;
  dataTestId?: string;
}

export const ExpandableTextBlock = (props: IExpandableTextBlock) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [customTextHeight, setCustomTextHeight] = useState(0);

  const { dataTestId, children } = props;
  const isMobile = useIsMobile();

  const customTextRef = useRef<HTMLDivElement | null>(null);
  const customTextVisibilityLength = isMobile ? 300 : 440;
  const customTextLength = customTextRef.current?.innerText.length ?? 0;
  const customTextClientHeight = customTextRef.current?.clientHeight ?? 0;
  const isMoreTextVisible = customTextLength >= customTextVisibilityLength;

  const onResize = useCallback(() => {
    window.addEventListener('resize', () => setCustomTextHeight(customTextClientHeight));
  }, [customTextClientHeight]);

  useEffect(() => {
    setCustomTextHeight(customTextClientHeight);
    onResize();
  }, [customTextClientHeight, customTextRef, onResize]);

  const expandButton = (
    <StyledLink
      underline="none"
      ownerState={{ expanded: isExpanded }}
      data-testid={dataTestId}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {isExpanded ? (
        <Localization
          id={TranslationKey.hide}
          defaultMessage={defaultTranslations.hide}
        />
      ) : (
        <Localization
          id={TranslationKey.readMore}
          defaultMessage={defaultTranslations.readMore}
        />
      )}
    </StyledLink>
  );

  return (
    <RootDiv>
      <StyledTypography
        variant="caption"
        align="left"
        ownerState={{
          expanded: isExpanded,
          visibleMoreButton: isMoreTextVisible,
          height: customTextHeight,
        }}
      >
        <div ref={customTextRef}>{children}</div>
        {isMoreTextVisible ? <OverlayWrapper ownerState={{ expanded: isExpanded }} /> : null}
      </StyledTypography>
      {isMoreTextVisible ? expandButton : null}
    </RootDiv>
  );
};
